import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Users, Clock, Zap } from "lucide-react";
import { Link } from "react-router-dom";

interface CoinCardProps {
  id: string;
  name: string;
  ticker: string;
  image?: string | null;
  marketCap: number;
  targetMarketCap?: number | null;
  progress?: number | null;
  price?: number | null;
  holders?: number | null;
  createdAt?: number | null;
  isLocked?: boolean | null;
  isPremium?: boolean | null;
  priceChange?: number;
}

export function CoinCard({
  id,
  name,
  ticker,
  image,
  marketCap,
  targetMarketCap,
  progress,
  price,
  holders,
  createdAt,
  isLocked,
  isPremium,
  priceChange = 0,
}: CoinCardProps) {
  const formatMarketCap = (mc: number) => {
    if (mc >= 1000000) return `$${(mc / 1000000).toFixed(2)}M`;
    if (mc >= 1000) return `$${(mc / 1000).toFixed(1)}K`;
    return `$${mc.toFixed(2)}`;
  };

  const formatTime = (timestamp: number | null | undefined) => {
    if (!timestamp) return "Just now";
    const diff = Date.now() - timestamp * 1000; // Convert seconds to ms if needed
    const diffMs = timestamp > 1e12 ? Date.now() - timestamp : diff;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const days = Math.floor(diffMs / 86400000);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return "Just now";
  };

  const progressValue = progress ?? (targetMarketCap ? (marketCap / targetMarketCap) * 100 : 0);

  return (
    <Link
      to={`/coin/${id}`}
      className={cn(
        "group block p-4 rounded-xl bg-card border border-border/50 transition-all duration-300",
        "hover:border-primary/50 hover-lift",
        isPremium && "border-primary/30 ring-1 ring-primary/20"
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        {/* Coin Image */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 rounded-xl bg-secondary overflow-hidden">
            {image ? (
              <img
                src={image}
                alt={name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-lg font-display font-bold text-primary">
                {ticker.charAt(0)}
              </div>
            )}
          </div>
          {isPremium && (
            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
              <Zap className="w-3 h-3 text-primary-foreground" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
              {name}
            </h3>
            {isLocked && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-warning/20 text-warning">
                LOCKED
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground font-mono">${ticker}</p>
        </div>

        {/* Price Change */}
        <div
          className={cn(
            "flex items-center gap-1 text-sm font-mono font-medium",
            priceChange >= 0 ? "text-success" : "text-destructive"
          )}
        >
          {priceChange >= 0 ? (
            <TrendingUp className="w-3.5 h-3.5" />
          ) : (
            <TrendingDown className="w-3.5 h-3.5" />
          )}
          {Math.abs(priceChange).toFixed(1)}%
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Market Cap</p>
          <p className="font-mono font-semibold text-primary">
            {formatMarketCap(marketCap)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-0.5">Target</p>
          <p className="font-mono font-medium text-foreground">
            {targetMarketCap ? formatMarketCap(targetMarketCap) : "∞"}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">Progress to Unlock</span>
          <span className="font-mono text-primary">{progressValue.toFixed(1)}%</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full progress-gradient rounded-full transition-all duration-500"
            style={{ width: `${Math.min(progressValue, 100)}%` }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Users className="w-3.5 h-3.5" />
          <span>{holders ?? 0} holders</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" />
          <span>{formatTime(createdAt)}</span>
        </div>
      </div>
    </Link>
  );
}